import { useState } from 'react';
import { Plus, X, Search, UserPlus, Mail, Phone, AlertTriangle, Info, Check, Edit2, Trash2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api, externalContactEndpoints, getApiErrorMessage } from '@/lib/api';

const NO_CONTACT_TYPE = '__none__';

const normalizeReporter = (source = {}, fallback = {}) => ({
  ...fallback,
  ...source,
  id: source.id ?? source.external_contact_id ?? source.contact_id ?? fallback.id,
  uuid: source.uuid ?? fallback.uuid,
  name: source.name ?? fallback.name ?? '',
  nickname: source.nickname ?? source.nick_name ?? fallback.nickname ?? '',
  phone: source.phone ?? source.phone_number ?? fallback.phone ?? '',
  email: source.email ?? fallback.email ?? '',
  organization: source.organization ?? source.organisation ?? source.company ?? fallback.organization ?? '',
  externalContactTypeId: String(source.external_contact_type_id ?? source.contact_type_id ?? source.contactTypeId ?? source.contact_type ?? source.contact_type_key ?? source.contactType ?? source.category ?? fallback.externalContactTypeId ?? ''),
});

const reporterPayload = ({ name, nickname, phone, email, organization, externalContactTypeId }) => ({
  name,
  nickname: nickname || null,
  phone: phone || null,
  email: email || null,
  organization,
  external_contact_type_id: toApiId(externalContactTypeId),
  contact_type_id: toApiId(externalContactTypeId),
});

const uniqueIdsByString = (ids = []) => Array.from(
  new Map(ids.map(id => [String(id), id])).values()
);

const toLocalPhone = (value = '') => {
  const digits = String(value).replace(/\D/g, '');
  const withoutCountryCode = digits.length > 9 && digits.startsWith('351') ? digits.slice(3) : digits;
  return withoutCountryCode.slice(0, 9);
};

const contactTypeLabel = (contactTypes = [], id) => {
  const type = contactTypes.find(item => String(item.id) === String(id));
  return type?.name || type?.label || '';
};

const toApiId = (value) => {
  if (!value) return null;
  const number = Number(value);
  return Number.isInteger(number) ? number : value;
};

/**
 * AuthorizedReportersPicker
 * --------------------------
 * Gere o pool global de Alertantes Autorizados E a seleção que aplica a um
 * Tipo de Ocorrência. Estes utilizadores NÃO fazem login no sistema —
 * existem apenas para serem reconhecidos pelo número/telefone quando reportam
 * ocorrências (ex: phoneMode = 'registered_only').
 *
 * Props:
 *   pool                  Array<{id, name, nickname, phone, email}>
 *   onPoolChange(nextArr) — atualiza o pool global (criar/editar/eliminar)
 *   selectedIds           Array<number> — alertantes associados ao tipo de ocorrência atual
 *   onSelectedIdsChange   — atualiza a seleção
 *   contactTypes          Array<{id, name}> — tipos de contacto externo disponiveis
 *   contactTypesLoading   boolean — se true, bloqueia o campo de tipo enquanto carrega
 *   restrictedMode        boolean — se true, mostra aviso 'phoneMode=registered_only'
 *   testIdPrefix          string — prefixo para data-testids (default 'reporters')
 */
export const AuthorizedReportersPicker = ({
  pool,
  onPoolChange,
  selectedIds,
  onSelectedIdsChange,
  contactTypes = [],
  contactTypesLoading = false,
  restrictedMode = false,
  testIdPrefix = 'reporters',
}) => {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [organization, setOrganization] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [externalContactTypeId, setExternalContactTypeId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetInputs = () => {
    setName('');
    setNickname('');
    setOrganization('');
    setPhone('');
    setEmail('');
    setExternalContactTypeId('');
    setEditingId(null);
  };

  const startCreate = () => { resetInputs(); setCreating(true); };
  const startEdit = (r) => {
    setEditingId(r.id);
    setName(r.name);
    setNickname(r.nickname || '');
    setOrganization(r.organization || '');
    setPhone(toLocalPhone(r.phone));
    setEmail(r.email || '');
    setExternalContactTypeId(r.externalContactTypeId ? String(r.externalContactTypeId) : '');
    setCreating(true);
  };

  const submitForm = async () => {
    if (!name.trim()) { toast.error('Nome é obrigatório.'); return; }
    if (!organization.trim()) { toast.error('Organizacao e obrigatoria.'); return; }
    if (!phone.trim() && !email.trim()) { toast.error('Indique pelo menos telefone ou email.'); return; }
    if (phone.trim() && !/^\d{9}$/.test(phone.trim())) { toast.error('O telefone deve ter 9 dígitos.'); return; }
    const formData = {
      name: name.trim(),
      nickname: nickname.trim(),
      organization: organization.trim(),
      phone: phone.trim(),
      email: email.trim(),
      externalContactTypeId,
    };

    setSubmitting(true);
    try {
      if (editingId) {
        const current = pool.find(r => String(r.id) === String(editingId));
        const response = await api.post(externalContactEndpoints.update, {
          id: editingId,
          external_contact_id: editingId,
          uuid: current?.uuid,
          ...reporterPayload(formData),
        });
        const updated = normalizeReporter(response.data?.data ?? response.data?.external_contact ?? response.data?.contact ?? response.data, {
          ...current,
          ...formData,
        });
        onPoolChange(pool.map(r => String(r.id) === String(editingId) ? updated : r));
        toast.success(`Alertante "${updated.name}" atualizado.`);
      } else {
        const response = await api.post(externalContactEndpoints.create, reporterPayload(formData));
        const created = normalizeReporter(response.data?.data ?? response.data?.external_contact ?? response.data?.contact ?? response.data, {
          id: Date.now(),
          ...formData,
        });
        onPoolChange([...pool, created]);
        onSelectedIdsChange(uniqueIdsByString([...(selectedIds || []), created.id]));
        toast.success(`Alertante "${created.name}" adicionado e selecionado.`);
      }
      resetInputs();
      setCreating(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível guardar o contacto externo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const removeFromPool = async (id) => {
    if (!window.confirm('Remover este alertante do sistema? Será também removido de todos os tipos de ocorrência onde está selecionado.')) return;
    const current = pool.find(r => String(r.id) === String(id));
    setSubmitting(true);
    try {
      await api.post(externalContactEndpoints.delete, {
        id,
        external_contact_id: id,
        uuid: current?.uuid,
      });
      onPoolChange(pool.filter(r => String(r.id) !== String(id)));
      onSelectedIdsChange((selectedIds || []).filter(x => String(x) !== String(id)));
      toast.success('Alertante removido.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível remover o contacto externo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (id) => {
    const has = (selectedIds || []).some(x => String(x) === String(id));
    onSelectedIdsChange(has ? selectedIds.filter(x => String(x) !== String(id)) : [...(selectedIds || []), id]);
  };

  const filtered = pool.filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [r.name, r.nickname, r.organization, r.phone, r.email].filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const selectedCount = new Set((selectedIds || []).map(id => String(id))).size;

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50 overflow-hidden" data-testid={`${testIdPrefix}-picker`}>
      <div className="px-3 py-2 bg-white border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus size={15} className="text-blue-600" />
          <Label className="text-sm font-semibold text-slate-800">Alertantes autorizados</Label>
          <Badge variant="outline" className="text-[10px]">
            {selectedCount} de {pool.length} selecionado{selectedCount === 1 ? '' : 's'}
          </Badge>
        </div>
        {!creating && (
          <Button type="button" size="sm" variant="outline" onClick={startCreate} disabled={submitting} data-testid={`${testIdPrefix}-add-btn`}>
            <Plus size={13} className="mr-1" />Novo alertante
          </Button>
        )}
      </div>

      {/* Aviso quando o tipo está em modo restrito */}
      <div className={`px-3 py-2 text-[11px] flex items-start gap-2 ${restrictedMode ? 'bg-amber-50 text-amber-800 border-b border-amber-200' : 'bg-slate-50 text-slate-500 border-b border-slate-100'}`}>
        {restrictedMode ? <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" /> : <Info size={13} className="mt-0.5 flex-shrink-0" />}
        <span>
          {restrictedMode
            ? <>O tipo está em modo <strong>Apenas números registados</strong>. Apenas os alertantes selecionados abaixo poderão reportar ocorrências deste tipo.</>
            : <>Estes utilizadores não fazem login no sistema. Servem apenas para serem reconhecidos pelo telefone/email ao reportar ocorrências, e para receber notificações se necessário.</>}
        </span>
      </div>

      {/* Mini-form criar/editar */}
      {creating && (
        <div className="px-3 py-3 bg-white border-b border-slate-200 space-y-2" data-testid={`${testIdPrefix}-form`}>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Nome *" value={name} onChange={e => setName(e.target.value)} data-testid={`${testIdPrefix}-form-name`} />
            <Input placeholder="Alcunha (opcional)" value={nickname} onChange={e => setNickname(e.target.value)} data-testid={`${testIdPrefix}-form-nickname`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] font-medium text-slate-600">Tipo de contacto</Label>
              <Select
                value={externalContactTypeId || NO_CONTACT_TYPE}
                onValueChange={value => setExternalContactTypeId(value === NO_CONTACT_TYPE ? '' : value)}
                disabled={submitting || contactTypesLoading}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-form-contact-type`}>
                  <SelectValue placeholder={contactTypesLoading ? 'A carregar tipos...' : 'Sem tipo'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTACT_TYPE}>Sem tipo</SelectItem>
                  {contactTypes.map(type => (
                    <SelectItem key={type.id} value={String(type.id)}>
                      {type.name || type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-medium text-slate-600">Organizacao *</Label>
              <div className="relative">
                <Building2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  placeholder="Empresa..."
                  value={organization}
                  onChange={e => setOrganization(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  data-testid={`${testIdPrefix}-form-organization`}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <Phone size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input placeholder="912345678" value={phone} onChange={e => setPhone(toLocalPhone(e.target.value))} inputMode="numeric" maxLength={9} className="pl-7" data-testid={`${testIdPrefix}-form-phone`} />
            </div>
            <div className="relative">
              <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input placeholder="email@..." value={email} onChange={e => setEmail(e.target.value)} className="pl-7" data-testid={`${testIdPrefix}-form-email`} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => { resetInputs(); setCreating(false); }} disabled={submitting}>Cancelar</Button>
            <Button type="button" size="sm" onClick={submitForm} className="bg-blue-600 hover:bg-blue-700" disabled={submitting} data-testid={`${testIdPrefix}-form-submit`}>
              {editingId ? 'Guardar' : <><Plus size={12} className="mr-1" />Adicionar</>}
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      {pool.length > 0 && (
        <div className="px-3 py-2 bg-white border-b border-slate-100 relative">
          <Search size={13} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Pesquisar nome, alcunha, telefone, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-7 h-8 text-xs"
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
      )}

      {/* Lista */}
      <div className="max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-center text-xs text-slate-400 italic py-6 px-3">
            {pool.length === 0 ? 'Ainda não há alertantes registados. Clique em "Novo alertante" para criar o primeiro.' : 'Sem resultados para a pesquisa.'}
          </p>
        ) : filtered.map(r => {
          const checked = (selectedIds || []).some(id => String(id) === String(r.id));
          return (
            <div
              key={r.id}
              className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 transition-colors ${checked ? 'bg-blue-50/40' : 'bg-white hover:bg-slate-50'}`}
              data-testid={`${testIdPrefix}-row-${r.id}`}
            >
              <button
                type="button"
                onClick={() => toggle(r.id)}
                disabled={submitting}
                data-testid={`${testIdPrefix}-toggle-${r.id}`}
                className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300 hover:border-blue-400'}`}
                aria-label={checked ? 'Desselecionar' : 'Selecionar'}
              >
                {checked && <Check size={13} className="text-white" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <p className="text-sm font-medium text-slate-900">{r.name}</p>
                  {r.nickname && <Badge variant="outline" className="text-[9px] uppercase">{r.nickname}</Badge>}
                  {r.externalContactTypeId && (
                    <Badge variant="outline" className="text-[9px]">
                      {contactTypeLabel(contactTypes, r.externalContactTypeId) || 'Tipo'}
                    </Badge>
                  )}
                </div>
                {r.organization && (
                  <p className="text-[11px] text-slate-600 truncate">{r.organization}</p>
                )}
                <div className="flex items-center gap-3 text-[11px] text-slate-500 truncate">
                  {r.phone && <span className="flex items-center gap-1"><Phone size={10} />{r.phone}</span>}
                  {r.email && <span className="flex items-center gap-1 truncate"><Mail size={10} />{r.email}</span>}
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(r)} disabled={submitting} title="Editar">
                <Edit2 size={12} className="text-slate-500" />
              </Button>
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeFromPool(r.id)} disabled={submitting} title="Remover do sistema">
                <Trash2 size={12} className="text-red-500" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
